<template>
  <v-dialog v-model="open" max-width="1000">
    <v-card class="publish-logs-dialog-card">
      <v-card-title class="d-flex align-center justify-space-between publish-logs-dialog-header">
        <div class="d-flex flex-column">
          <div class="text-h6">Publish Logs</div>
          <div class="text-caption text-grey">
            <span v-if="jazelId">Dealer: {{ jazelId }}</span>
            <span v-if="jazelId && correlationId"> · </span>
            <span v-if="correlationId">Correlation ID: {{ correlationId }}</span>
          </div>
          <div class="text-caption text-grey mt-1 d-flex flex-wrap align-center ga-2">
            <template v-if="jobLoading">
              <v-progress-circular indeterminate color="primary" size="12" />
              <span>Loading run details…</span>
            </template>
            <template v-else>
              <span v-if="effectiveTriggeredBy">
                Triggered by:
                <span class="font-weight-medium">{{ effectiveTriggeredBy }}</span>
                <span v-if="effectiveFeed" class="text-grey">({{ effectiveFeed }})</span>
                <span v-else-if="effectiveTriggerType && effectiveTriggerType !== String(effectiveTriggeredBy).trim()" class="text-grey">
                  ({{ effectiveTriggerType }})
                </span>
              </span>

              <span v-if="feedFileKey">·</span>
              <span v-if="feedFileKey" class="d-flex align-center ga-1" style="min-width: 0;">
                Feed file:
                <v-btn
                  size="x-small"
                  variant="text"
                  class="pa-0 text-none"
                  append-icon="mdi-download"
                  :loading="downloadLoading"
                  :aria-label="`Download feed file ${feedFileName || 'file'}`"
                  @click.stop="downloadFeedFile"
                >
                  <span class="text-truncate" style="max-width: 360px" :title="feedFileKey">
                    {{ feedFileName || feedFileKey }}
                  </span>
                </v-btn>
              </span>
            </template>
          </div>
          <div v-if="jobError" class="text-caption text-warning mt-1">
            {{ jobError }}
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" aria-label="Close publish logs" @click="open = false" />
      </v-card-title>

      <v-card-text class="publish-logs-dialog-body">
        <v-alert v-if="!correlationId" type="info" variant="tonal" density="comfortable">
          No correlation ID available for this history row.
        </v-alert>

        <template v-else>
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
            <div class="text-caption text-grey">
              {{ entries.length }} log {{ entries.length === 1 ? 'entry' : 'entries' }}
              <span v-if="maxLines"> (showing last {{ maxLines }})</span>
            </div>
            <div class="d-flex align-center ga-2">
              <v-btn
                size="small"
                variant="tonal"
                prepend-icon="mdi-refresh"
                :disabled="!correlationId"
                :loading="loading"
                @click="reload"
              >
                Refresh
              </v-btn>
            </div>
          </div>

          <!-- Feed Guardrail callout -->
          <v-alert
            v-if="isFlaggedLargeDelta"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-shield-alert-outline"
            class="mb-3"
          >
            <div class="text-subtitle-2 font-weight-bold mb-1">
              Feed Blocked — Large Inventory Drop ({{ guardrailResult?.deltaPercent ?? '?' }}%)
            </div>
            <div class="text-body-2">
              Previous: <strong>{{ guardrailResult?.previousVinCount ?? '?' }} VINs</strong>
              &rarr; New: <strong>{{ guardrailResult?.newVinCount ?? '?' }} VINs</strong>
              &nbsp;&bull;&nbsp; Threshold: {{ guardrailResult?.thresholdPercent ?? 50 }}%
            </div>
            <div class="text-caption text-grey mt-1">
              The feed file is stored in S3 but was NOT published to OpenSearch.
              Use the <strong>Approve &amp; Reprocess</strong> or <strong>Discard</strong>
              actions on the dashboard to resolve this flag.
            </div>
          </v-alert>

          <PublishLogsTimeline
            :logs="humanReadableLogs"
            :raw-json="rawJson"
            :loading="loading"
            :error="error"
            :show-debug-panels="showDebugPanels"
          />
        </template>
      </v-card-text>

      <v-card-actions class="publish-logs-dialog-footer">
        <v-spacer />
        <v-btn @click="open = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { getPublishLogs, type PublishLogsResponse } from '@/services/publishHistoryService'
import { summarizePublishLogs } from '@/utils/publishLogsSummary'
import PublishLogsTimeline from '@/components/PublishLogsTimeline.vue'
import { parsePublishLogsToHumanReadable, type PublishHumanLog } from '@/utils/publishLogsTimeline'
import { checkImportFeedJobByCorrelationId } from '@/services/publishService'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    endpoint: string
    jazelId: string
    correlationId: string | null
    sessionToken?: string
    maxLines?: number
  }>(),
  {
    maxLines: 200,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
}>()

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const loading = ref(false)
const error = ref('')
const raw = ref('')
const entries = ref<unknown[]>([])
const showDebugPanels = import.meta.env.DEV
const jobLoading = ref(false)
const jobError = ref('')
const jobMeta = ref<{
  status?: string
  triggerType?: string
  requestedByUsername?: string
  feed?: string
  fileKey?: string
  result?: any | null
} | null>(null)
const downloadLoading = ref(false)

const cache = new Map<string, PublishLogsResponse>()

const cacheKey = computed(() => {
  const cid = String(props.correlationId || '').trim()
  const jlz = String(props.jazelId || '').trim()
  return `${jlz}#${cid}#${String(props.maxLines || '')}`
})

const summary = computed(() => summarizePublishLogs(entries.value))

const humanReadableLogs = computed<PublishHumanLog[]>(() => parsePublishLogsToHumanReadable(entries.value).logs)
const rawJson = computed(() => JSON.stringify(entries.value || [], null, 2))

const effectiveTriggeredBy = computed(() => {
  const triggerType = String(jobMeta.value?.triggerType || '').toLowerCase()
  const usernameFromJob = String(jobMeta.value?.requestedByUsername || '').trim()
  const usernameFromLogs = String(summary.value.requestedByUsername || '').trim()
  const username = usernameFromJob || usernameFromLogs

  if (triggerType === 'feed') return 'feed'
  if (triggerType === 'user') return username || 'anonymous'
  return username || (triggerType ? triggerType : '')
})

const effectiveTriggerType = computed(() => {
  const t = String(jobMeta.value?.triggerType || '').trim()
  return t || ''
})

function normalizeFeedLabel(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return ''
  // common forms: "import_vauto" -> "vauto"
  return s.startsWith('import_') ? s.slice('import_'.length) : s
}

const feedFromJob = computed(() => normalizeFeedLabel(String(jobMeta.value?.feed || '').trim()))
const feedFromLogs = computed(() => {
  const entriesArr = Array.isArray(entries.value) ? (entries.value as any[]) : []
  for (const e of entriesArr) {
    const msg = String(e?.message || e?.msg || '').trim()
    if (!msg) continue
    const m1 = msg.match(/feedSource=([a-z0-9_-]+)/i)
    if (m1?.[1]) return normalizeFeedLabel(m1[1])
    const m2 = msg.match(/(?:^|[\s(,;])feed=([a-z0-9_-]+)/i)
    if (m2?.[1]) return normalizeFeedLabel(m2[1])
  }
  return ''
})

const effectiveFeed = computed(() => feedFromJob.value || feedFromLogs.value)

const feedFileKey = computed(() => String(jobMeta.value?.fileKey || '').trim())

// Guardrail info (only present when status = FLAGGED_LARGE_DELTA)
const isFlaggedLargeDelta = computed(() => jobMeta.value?.status === 'FLAGGED_LARGE_DELTA')
const guardrailResult = computed(() => jobMeta.value?.result ?? null)
const feedFileName = computed(() => {
  const key = feedFileKey.value
  if (!key) return ''
  return key.includes('/') ? key.split('/').pop() || key : key
})

async function fetchLogs(force = false): Promise<void> {
  error.value = ''
  const correlationId = String(props.correlationId || '').trim()
  if (!correlationId) {
    raw.value = ''
    entries.value = []
    return
  }

  const key = cacheKey.value
  if (!force && cache.has(key)) {
    const cached = cache.get(key)!
    raw.value = cached.raw || ''
    entries.value = Array.isArray(cached.entries) ? cached.entries : []
    return
  }

  loading.value = true
  try {
    const resp = await getPublishLogs(props.endpoint, props.jazelId, correlationId, { maxLines: props.maxLines })
    cache.set(key, resp)
    raw.value = resp.raw || ''
    entries.value = Array.isArray(resp.entries) ? resp.entries : []
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
    raw.value = ''
    entries.value = []
  } finally {
    loading.value = false
  }
}

const reload = async () => {
  await fetchLogs(true)
}

async function fetchJobMeta(): Promise<void> {
  jobError.value = ''
  const cid = String(props.correlationId || '').trim()
  if (!cid) {
    jobMeta.value = null
    return
  }

  jobLoading.value = true
  try {
    const res = await checkImportFeedJobByCorrelationId(props.endpoint, cid)
    jobMeta.value = res
  } catch (e) {
    jobMeta.value = null
    jobError.value = e instanceof Error ? e.message : String(e)
  } finally {
    jobLoading.value = false
  }
}

async function downloadFeedFile(): Promise<void> {
  const key = feedFileKey.value
  const jazelId = String(props.jazelId || '').trim()
  if (!key || !jazelId) return

  downloadLoading.value = true
  try {
    const qs = new URLSearchParams({
      jazelId,
      key,
      expiresIn: '900',
    })
    const url = `${props.endpoint}/reports/import-feed-file-presign?${qs.toString()}`
    const headers: Record<string, string> = {}
    if (props.sessionToken) headers.Authorization = `Bearer ${props.sessionToken}`
    const res = await fetch(url, { method: 'GET', headers })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `HTTP ${res.status} ${res.statusText}`)
    }
    const data = await res.json().catch(() => null)
    const presignedUrl = data?.presignedUrl
    if (typeof presignedUrl !== 'string' || !presignedUrl) throw new Error('Unable to generate download link')
    window.open(presignedUrl, '_blank', 'noopener')
  } finally {
    downloadLoading.value = false
  }
}

watch(
  () => open.value,
  async (isOpen) => {
    if (!isOpen) return
    await fetchJobMeta()
    await fetchLogs(false)
  },
)

watch(
  () => props.correlationId,
  async () => {
    if (!open.value) return
    await fetchJobMeta()
    await fetchLogs(false)
  },
)
</script>

<style scoped>
.publish-logs-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: min(86vh, 820px);
}
.publish-logs-dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #eef2f7;
}
.publish-logs-dialog-body {
  overflow-y: auto;
}
.publish-logs-dialog-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #fff;
  border-top: 1px solid #eef2f7;
}
</style>

